import {Connect, ConnectInstaller} from "@ibm-aspera/connect-sdk-js";
import {ConnectStatusStrings, TransferInfo, VersionOutput} from "@ibm-aspera/connect-sdk-js/src/core/types";
import {
    ConnectError,
    ConnectSpec,
    StartTransferOutput,
    TransferSpec,
} from "@ibm-aspera/connect-sdk-js/dist/esm/core/types";
import { useRef } from "react";
import {Resource, ResourceUploadState, UploadStatus} from "../api";
import {GetAsperaApi} from "../api/apiConfig";
import { useProjects } from "../Behaviors/projects";
import { useStore } from "../State/zustandStore";

interface AitTransferSpec extends TransferSpec {
    s3_path: string;
    is_auto_upload: boolean;
}

const connectClient = new Connect();

async function getVersion() : Promise<VersionOutput> {
    return new Promise((resolve, reject) => {
        if (connectClient.getStatus() === Connect.STATUS.RUNNING) {
            connectClient.version({
                success: (v) => resolve(v),
                error: (e) => reject(e),
            });
        }
    });
}

async function handleConnection(type: any, data: ConnectStatusStrings ) {
    const installation = new ConnectInstaller();

    if (data === Connect.STATUS.FAILED) {
        installation.showInstall();
    }

    if (data === Connect.STATUS.RUNNING) {
        const version = await getVersion();

        if (!version.version.startsWith("4.2.")) {
            installation.showUpdate();
        }
    }
}

connectClient.addEventListener(Connect.EVENT.STATUS, handleConnection);

connectClient.initSession(`${process.env.REACT_APP_NAMESPACE}-42wbC6tWj94L`);

async function getTransfers() : Promise<TransferInfo[]> {
    return new Promise((resolve, reject) => {
        connectClient.getAllTransfers({
            success: (transfers) => resolve(transfers.transfers),
            error: (e) => reject(e),
        });
    });
}

export const useAsperaApi = () => {
    const {updateUploadStates, startIngest, revalidateProject, recentJobFinished } = useProjects(undefined);
    const {projects, aspera} = useStore();
    const {setAllUploadProgress, clearUploadProgress} = aspera;
    const projectsRef = useRef(projects);

    projectsRef.current = projects;

    async function createTransferRequest(projectId: string, resources: Resource[]) {
        // When the transfer starts, set upload status to zero percent on all the files.
        // If the ui doesn't update these outside of the callback closure, then they
        // won't be updated inside of it
        async function transferStarted(output: StartTransferOutput) {
            for (const spec of output.transfer_specs) {
                const uploadStates: ResourceUploadState[] = [];

                for (const file of spec.transfer_spec.paths) {
                    uploadStates.push({ localPath: file.source, uploadStatus: UploadStatus.InProgress, uploadPercentage: 0 });
                };
                await updateUploadStates(projectId, uploadStates);
            }
            return output.transfer_specs[0].uuid;
        }

        const transfers = await getTransfers();
        const projectTransfer = transfers.find(t => t.transfer_spec.cookie === projectId) ;

        if (projectTransfer) {
            if (["completed", "cancelled", "removed", "failed"].includes(projectTransfer.status)) { // Transfer is not active, okay to place new one
                projectsRef.current.logProjectEvent(projectId!, `Previous transfer ${projectTransfer.uuid} has status ${projectTransfer.status}`);
                clearUploadProgress(projectId);
            } else { // Transfer is active, don't start a new one
                if (((projectTransfer.transfer_spec as AitTransferSpec).is_auto_upload)) {
                    projectsRef.current.logProjectEvent(projectId!, `Existing transfer is auto upload ${projectTransfer.uuid}`);
                } else {
                    projectsRef.current.logProjectEvent(projectId!, `Can't start transfer because of existing transfer ${projectTransfer.uuid}`);
                    return Promise.resolve();
                }
            }
        }

        const api = await GetAsperaApi();
        const response = await api.asperaUploadSpecPost(projectId!, resources);

        if (response.status === 204) {
            const updatedProject = await updateUploadStates(projectId, []);

            clearUploadProgress(projectId);
            projectsRef.current.logProjectEvent(projectId!, "No files to transfer");

            // the api determines when an ingestion can start based on the upload status of
            // the effective resources.  If the startIngest flag is set, start the ingestion.
            if (updatedProject?.startIngestion ?? false) {
                const selectedCprsShipments = projectsRef.current.selectedCprsShipments.get(updatedProject.projectId!);

                await startIngest(updatedProject, "start ingest flag", selectedCprsShipments);

            } else if (!updatedProject.ingestHasStarted) { // Otherwise revalidate unless ingest has already started
                await revalidateProject("No files to transfer", updatedProject);
            }
            return Promise.resolve();
        }

        const transferSpec = response.data;
        return new Promise((resolve, reject) => {
            const t = transferSpec as TransferSpec;

            connectClient.startTransfer(t, transferSpec as ConnectSpec, {
                success: (transfer) => {
                    projectsRef.current.logProjectEvent(projectId!, `Created transfer ${transfer.transfer_specs[0].uuid} with ${transfer.transfer_specs[0].transfer_spec.paths.length} files`);
                    resolve(transferStarted(transfer));
                },
                error: async function (error: ConnectError) {
                    const failedUploadStates: ResourceUploadState[] = [];
                    const errorDescription = `Upload error: ${error.error.user_message}`;
                    const file = transferSpec.paths?.find(r => error.error.user_message.includes(r.source!));

                    failedUploadStates.push({ localPath: file?.source, uploadStatus: UploadStatus.Unknown, uploadPercentage: 0, errorCode: error.error.code, errorDescription: errorDescription });
                    await updateUploadStates(projectId, failedUploadStates);

                    clearUploadProgress(projectId);
                    projectsRef.current.logProjectEvent(projectId!, `Failed to start transfer ${error.error.user_message}`);

                    resolve("failed");
                },
            });
        });
    }

    async function handleTransferEvents(type: any, data: { transfers: TransferInfo[]; }) {

        for (const transfer of data.transfers) {
            const projectId = transfer.transfer_spec.cookie;

            if (!projectId) {
                console.error(`Aspera transfer with no project id ${transfer.uuid}`);
                return;
            }

            try {
                switch (transfer.status) {
                case "running":
                    // Only update file status if it is for the currently selected project
                    if (projectsRef.current.selectedProjectId === projectId) {
                        const statuses = new Map<string, number>();

                        for (const file of transfer.files?.filter(f => f.bytes_written > 0)) {
                            statuses.set(file.file, file.bytes_written / file.bytes_expected);
                        }
                        setAllUploadProgress(projectId, statuses);
                    }

                    break;
                case "completed":
                    const uploadStates: ResourceUploadState[] = [];

                    for (const file of transfer.files?.filter(f => f.bytes_written > 0)) {
                        const filePath = transfer.transfer_spec.paths.find(p => p.source === file.file);

                        if (filePath) {
                            const uploadPercentage = file.bytes_written / file.bytes_expected;

                            uploadStates.push({ localPath: file.file, uploadStatus: (uploadPercentage === 1 ? UploadStatus.Complete : UploadStatus.Unknown), uploadPercentage: uploadPercentage, s3Path: (transfer.transfer_spec as AitTransferSpec).s3_path });
                        }
                    }

                    const updatedProject = await updateUploadStates(projectId, uploadStates);

                    await connectClient.removeTransfer(transfer.uuid);
                    clearUploadProgress(projectId);
                    projectsRef.current.logProjectEvent(projectId!, `Completed transfer ${transfer.uuid}`);

                    const prevJobFinished = await recentJobFinished(updatedProject.jobId!);

                    if (prevJobFinished) {
                        // the api determines when an ingestion can start based on the upload status of
                        // the effective resources.  If the startIngest flag is set, start the ingestion.
                        if (updatedProject?.startIngestion ?? false) {
                            const selectedCprsShipments = projectsRef.current.selectedCprsShipments.get(updatedProject.projectId!);

                            await startIngest(updatedProject, "start ingest flag", selectedCprsShipments);

                        } else if (!updatedProject.ingestHasStarted) { // Otherwise revalidate unless ingest has already started
                            await revalidateProject("uploads completed", updatedProject);
                        }
                    }

                    break;
                case "failed":
                case "cancelled":
                    const failedUploadStates: ResourceUploadState[] = [];

                    for (const file of transfer.files ?? []) {
                        if (file) {
                            const filePath = transfer.transfer_spec.paths.find(p => p.source === file.file);

                            const uploadPercentage = file.bytes_written / file.bytes_expected;

                            if (uploadPercentage === 1 && filePath) {
                                failedUploadStates.push({ localPath: file.file, uploadStatus: UploadStatus.Complete, uploadPercentage: 1, s3Path: (transfer.transfer_spec as AitTransferSpec).s3_path!});
                            } else {
                                // @ts-expect-error
                                const errorDescription = `Upload error: ${file.error.description.replace(/^(Source:)/, "") ?? "An excepected error occurred."}`;

                                // @ts-expect-error
                                failedUploadStates.push({ localPath: file.file, uploadStatus: UploadStatus.Unknown, uploadPercentage: 0, errorCode: file.error.code, errorDescription: errorDescription });
                            }
                        }
                    }

                    if (!transfer.files) { // When only one file, files won't be populated
                        // @ts-expect-error
                        const errorDescription = `Upload error: ${transfer.error_desc.replace(/^(Source:)/, "")} ${transfer.explorer_path}` ?? "An excepected error occurred.";

                        // @ts-expect-error
                        failedUploadStates.push({ localPath: transfer.explorer_path, uploadStatus: UploadStatus.Unknown, uploadPercentage: 0, errorCode: transfer.error_code, errorDescription: errorDescription });
                    }

                    const failedProject = await updateUploadStates(projectId, failedUploadStates);

                    await connectClient.removeTransfer(transfer.uuid);
                    clearUploadProgress(projectId);
                    projectsRef.current.logProjectEvent(projectId!, `Failed transfer ${transfer.uuid}`);
                    await revalidateProject("uploads failed or cancelled", failedProject);
                    break;
                // removed
                // initiating
                // queued
                // willretry
                default:
                    break;
                }
            } catch (error) {
                //@ts-expect-error
                projectsRef.current.logProjectEvent(projectId, `Aspera error ${transfer.uuid} status ${transfer.status} failed with the error:${error?.message}`);
            }
        }
    }

    function GetTransferEventForwarder() : (type: any, data: { transfers: TransferInfo[] }) => void {
        return handleTransferEvents;
    }

    function watchForAsperaEvents() {
        const forwarder = GetTransferEventForwarder();

        connectClient.addEventListener(Connect.EVENT.TRANSFER, forwarder);
    }

    function stopWatchingForAsperaEvents() {
        connectClient?.removeEventListener(Connect.EVENT.TRANSFER);
    }

    async function cancelUpload(projectId: string) {
        projectsRef.current.logProjectEvent(projectId, `User cancelled transfers for project ${projectId}`);

        const allTransfers = await getTransfers();
        const transfersForProject = allTransfers.filter(t => t.transfer_spec.cookie === projectId);

        transfersForProject.every(async transfer => {
            await connectClient.stopTransfer(transfer.uuid);
            await connectClient.removeTransfer(transfer.uuid);
            projectsRef.current.logProjectEvent(projectId, `Transfer ${transfer.uuid} cancelled`);
        });
    }

    async function refreshResourceUploadStatus(resources: Resource[]) {
        // resources.forEach(async resource => {
        //     const transfer = await getTransferForResource(resource);

        //     if (transfer) {
        //         await handleTransferEvents(null, { transfers: [transfer]});
        //     }
        // });
    }

    async function uploadResources(resources: Resource[], projectId: string) {
        return await createTransferRequest(projectId, resources);
    }

    async function uploadAll(projectId: string) {
        return await createTransferRequest(projectId, []);
    }
    return {
        watchForAsperaEvents,
        cancelUpload,
        uploadAll,
        stopWatchingForAsperaEvents,
        refreshResourceUploadStatus,
        uploadResources,
    };
};
